/**
 * Kane SSO frontend service (#174).
 *
 * Wraps the ProMedic MCP server endpoints that bridge ProMedic ↔ Kane OneDeck:
 *
 *   /api/auth/kane-handoff  — mints short-lived handoff JWT (+ persists link
 *                             server-side via prelink when refresh enabled)
 *   /api/auth/kane-refresh  — re-uses a stored refresh token for silent SSO
 *   /api/auth/kane-revoke   — best-effort link teardown on logout
 *
 * Refresh tokens NEVER enter the frontend. They are stored in the service-only
 * `oauth_identities` table and proxied through the mcp-server. The frontend
 * only sees the 5-min handoff JWT that rides in the popup URL fragment.
 *
 * Strategy on bot click: refresh first (cheap if a valid link exists);
 * fall back to handoff (mints a new link via Kane's /auth/sso-prelink).
 */

import { supabase } from './supabase';

const MCP_URL = process.env.REACT_APP_MCP_SERVER_URL;

async function authedFetch(path, options = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('No active session');
  if (!MCP_URL) throw new Error('REACT_APP_MCP_SERVER_URL not configured');
  return fetch(`${MCP_URL}${path}`, {
    ...options,
    headers: {
      ...(options.headers || {}),
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
  });
}

/**
 * Fetch a handoff token + Kane URL for the popup, preferring the silent
 * refresh path. Always resolves with { token, kaneUrl }, or throws when
 * both refresh and handoff fail (callers should fall back to legacy URL
 * params in that case).
 */
export async function getKaneHandoffToken() {
  // 1. Try the refresh path first — silent when a valid link exists.
  //    404 → feature flag off for this user; 410 → no link or expired;
  //    401 → reuse detected (link wiped server-side); 502 → Kane down.
  //    Any non-200 falls through to handoff.
  try {
    const r = await authedFetch('/api/auth/kane-refresh', {
      method: 'POST',
      body: '{}',
    });
    if (r.ok) {
      const body = await r.json();
      if (body?.token) {
        return { token: body.token, kaneUrl: body.kaneUrl };
      }
    }
  } catch (err) {
    // Network failures during refresh → continue to handoff, don't surface.
    // eslint-disable-next-line no-console
    console.debug('Kane refresh path errored, falling back to handoff:', err?.message);
  }

  // 2. Handoff: mints fresh JWT + (when feature flag on) server-side prelink
  //    persists a new refresh row in oauth_identities.
  const r = await authedFetch('/api/auth/kane-handoff', {
    method: 'POST',
    body: '{}',
  });
  if (!r.ok) {
    const body = await r.json().catch(() => ({}));
    throw new Error(body?.error?.message || `Handoff failed (${r.status})`);
  }
  const body = await r.json();
  if (!body?.token) throw new Error('Handoff response missing token');
  return { token: body.token, kaneUrl: body.kaneUrl };
}

/**
 * Best-effort link teardown. Called from AuthContext.signOut() before
 * the Supabase signOut. Never throws — logout must always succeed.
 */
export async function revokeKaneLink() {
  try {
    await authedFetch('/api/auth/kane-revoke', { method: 'POST', body: '{}' });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('Kane link revoke failed (non-blocking):', err?.message);
  }
}
